/**
 * 振听（furiten）追踪
 *
 * 三种振听（规则容易漏，必须精确实现）：
 *
 *   1. **舍牌振听**（永久，直到和牌或听牌张变化）
 *      自己的舍牌中有任何一张"当前听牌张" → 不能荣和
 *      注意：副露的牌不算（副露不构成舍牌振听）
 *
 *   2. **同巡振听**（仅本巡）
 *      本巡内有人打出自己的听牌张，但自己选择不荣和 → 本巡不能荣和
 *      下一巡摸牌后自动解除
 *
 *   3. **立直后振听**（永久）
 *      立直后，若 pass 了可荣和的牌 → 之后永久振听（不能再荣和）
 *
 * 自摸不受振听限制。
 */

import { tileIndex, type Tile } from '../tile.js';
import type { GameState, PlayerState, Seat } from './state.js';
import { handToCounts } from './rules.js';
import { analyzeWaits } from '../wait.js';

// ════════════════════════════════════════════════════════════════
// 类型
// ════════════════════════════════════════════════════════════════

/** 振听类型 */
export type FuritenType =
  | 'none'
  | 'discard' // 舍牌振听
  | 'temporary' // 同巡振听
  | 'riichi' // 立直后振听
  | 'multiple'; // 多种并存

/** 振听分析结果 */
export interface FuritenAnalysis {
  /** 是否振听 */
  readonly furiten: boolean;
  /** 振听类型 */
  readonly types: readonly FuritenType[];
  /** 说明（便于 UI 展示） */
  readonly reason: string;
  /** 触发振听的牌（索引） */
  readonly triggeringTiles: readonly number[];
}

/** 追踪状态（存在 GameState 之外，或作为 PlayerState 扩展） */
export interface FuritenTracker {
  /** 同巡振听标记：seat → 本巡内 pass 过的听牌张 */
  readonly temporaryFuriten: ReadonlyMap<Seat, readonly number[]>;
  /** 立直后振听：seat → 是否已触发 */
  readonly riichiFuriten: ReadonlySet<Seat>;
}

/** 空追踪器 */
export function emptyTracker(): FuritenTracker {
  return {
    temporaryFuriten: new Map(),
    riichiFuriten: new Set(),
  };
}

// ════════════════════════════════════════════════════════════════
// 核心分析
// ════════════════════════════════════════════════════════════════

/**
 * 分析某家的振听状态
 *
 * @param player 玩家状态
 * @param tracker 追踪器（同巡/立直后振听）
 */
export function analyzeFuriten(
  player: PlayerState,
  tracker: FuritenTracker = emptyTracker(),
): FuritenAnalysis {
  const counts = handToCounts(player.hand);
  const waits = analyzeWaits(counts, player.melds);
  const waitTiles = waits.waits.map((w) => w.tile);

  if (waitTiles.length === 0) {
    return { furiten: false, types: [], reason: '未听牌', triggeringTiles: [] };
  }

  const types: FuritenType[] = [];
  const triggers = new Set<number>();
  const reasons: string[] = [];

  // ── 1. 舍牌振听 ──
  const ownDiscards = new Set(player.discards.map(tileIndex));
  const discardTriggers = waitTiles.filter((w) => ownDiscards.has(w));
  if (discardTriggers.length > 0) {
    types.push('discard');
    discardTriggers.forEach((t) => triggers.add(t));
    reasons.push('自己的舍牌中含听牌张');
  }

  // ── 2. 同巡振听 ──
  const temp = tracker.temporaryFuriten.get(player.seat) ?? [];
  const tempTriggers = waitTiles.filter((w) => temp.includes(w));
  if (tempTriggers.length > 0) {
    types.push('temporary');
    tempTriggers.forEach((t) => triggers.add(t));
    reasons.push('本巡已见听牌张但未荣和');
  }

  // ── 3. 立直后振听 ──
  if (tracker.riichiFuriten.has(player.seat)) {
    types.push('riichi');
    waitTiles.forEach((t) => triggers.add(t));
    reasons.push('立直后pass过可荣和的牌');
  }

  const furiten = types.length > 0;
  return {
    furiten,
    types: types.length > 1 ? ['multiple'] : types,
    reason: furiten ? reasons.join('；') : '正常',
    triggeringTiles: [...triggers],
  };
}

// ════════════════════════════════════════════════════════════════
// 追踪器更新
// ════════════════════════════════════════════════════════════════

/**
 * 当某家 pass 了可荣和的牌时，记录同巡振听
 *
 * @param tracker 当前追踪器
 * @param seat pass 的玩家
 * @param tile 被 pass 的牌
 * @param player 该玩家状态（用于判断是否可荣和）
 */
export function recordPass(
  tracker: FuritenTracker,
  seat: Seat,
  tile: Tile,
  player: PlayerState,
): FuritenTracker {
  const counts = handToCounts(player.hand);
  const waits = analyzeWaits(counts, player.melds);
  const tileIdx = tileIndex(tile);

  // 只有"本来能荣和却 pass"才记录振听
  if (!waits.waits.some((w) => w.tile === tileIdx)) {
    return tracker; // pass 的不是听牌张，不构成振听
  }

  const map = new Map(tracker.temporaryFuriten);
  const existing = map.get(seat) ?? [];
  if (!existing.includes(tileIdx)) {
    map.set(seat, [...existing, tileIdx]);
  }

  // 立直后 pass → 永久振听
  const riichi = new Set(tracker.riichiFuriten);
  if (player.riichi) {
    riichi.add(seat);
  }

  return { temporaryFuriten: map, riichiFuriten: riichi };
}

/**
 * 某家摸牌后，解除其同巡振听
 *
 * 规则：同巡振听在"自己摸牌后"解除。
 */
export function clearTemporaryFuriten(
  tracker: FuritenTracker,
  seat: Seat,
): FuritenTracker {
  if (!tracker.temporaryFuriten.has(seat)) return tracker;
  const map = new Map(tracker.temporaryFuriten);
  map.delete(seat);
  return { ...tracker, temporaryFuriten: map };
}

/**
 * 检查某家是否处于振听状态（用于 canRon）
 */
export function isFuriten(
  player: PlayerState,
  tracker: FuritenTracker = emptyTracker(),
): boolean {
  return analyzeFuriten(player, tracker).furiten;
}

// ════════════════════════════════════════════════════════════════
// 与 GameState 集成
// ════════════════════════════════════════════════════════════════

/** 带振听追踪的游戏状态扩展 */
export interface GameStateWithFuriten extends GameState {
  readonly furitenTracker: FuritenTracker;
}

/** 从基础状态创建带追踪的状态 */
export function withFuritenTracker(state: GameState): GameStateWithFuriten {
  return { ...state, furitenTracker: emptyTracker() };
}

/**
 * 分析全部玩家的振听状态（UI 用）
 */
export function analyzeAllFuriten(
  state: GameStateWithFuriten,
): ReadonlyMap<Seat, FuritenAnalysis> {
  const result = new Map<Seat, FuritenAnalysis>();
  for (const p of state.players) {
    result.set(p.seat, analyzeFuriten(p, state.furitenTracker));
  }
  return result;
}

/** 振听类型的中文名 */
export function furitenTypeName(t: FuritenType): string {
  const names: Record<FuritenType, string> = {
    none: '无',
    discard: '舍牌振听',
    temporary: '同巡振听',
    riichi: '立直振听',
    multiple: '多重振听',
  };
  return names[t];
}
